import React, { useEffect, useState } from "react";
import {
  DeleteTenant,
  GetAllTenant,
} from "../../../services/SuperAdminServices/TenantService";
import { useAppContext } from "../../../context/AppContext";
import {
  renderLicenseIcon,
  TenantBasicDetailModel,
  TenantModel,
} from "./TenantModel";
import {
  Box,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Heading,
  Flex,
  Text,
  Tooltip,
  Spinner,
  Center,
  ModalContent,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
// import { LicenseType } from "../../AdminPages/Users/UserRoleEnum";
import { useNavigate } from "react-router-dom";
import { Icon, WarningIcon } from "@chakra-ui/icons";
import { BiShow } from "react-icons/bi";
import TenantDetailsForm from "./TenantDetailsForm";
import NotFound from "../../CommonPages/NotFound";
import { useRef } from "react";

const TenantList: React.FC = () => {
  const { accessToken, userData } = useAppContext();
  const [tenants, setTenants] = useState<TenantModel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await GetAllTenant(accessToken);
      setTenants(response);
    } catch (error) {
      console.error("Error fetching tenants:", error);
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = (tenantId: number) => {
    navigate(`/tenants/${tenantId}`);
  };

  // Add state to control modal
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] =
    useState<TenantBasicDetailModel | null>(null);

  const openTenantModal = () => {
    setSelectedTenant(null); // null means adding new
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  // delete model
  // delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTenantId, setDeleteTenantId] = useState<number | null>(null);

  // delete model
  const openDeleteModal = (tenantId: number) => {
    setDeleteTenantId(tenantId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteTenantId(null);
  };

  const confirmDelete = async () => {
    if (deleteTenantId !== null) {
      try {
        await DeleteTenant(deleteTenantId, 0, accessToken); // Only pass tenantId and token
        await fetchTenants(); // Refresh list
      } catch (error) {
        console.error("Error deleting tenant:", error);
      }
      closeDeleteModal();
    }
  };

  if (loading) {
    return (
      <Center h="100%">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (userData?.roleID !== 3) {
    return <NotFound />;
  }

  return (
    <>
      <Flex justify="space-between" align="center" mb={4} p={6}>
        <Heading as="h2">Tenant</Heading>
        <Button colorScheme="blue" onClick={openTenantModal}>
          <i className="bi bi-person-plus" style={{ marginRight: "8px" }}></i>
          Add Tenant
        </Button>
      </Flex>

      <Box
        p={6}
        shadow="md"
        borderWidth="1px"
        borderRadius={"15px"}
        backgroundColor={"#fff"}
      >
        <Table className="Table">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Description</Th>
              <Th>Email</Th>
              <Th>Phone Number</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tenants.map((user) => (
              <Tr key={user.Id}>
                <Td>{user.Name}</Td>
                <Td>{user.Description}</Td>
                <Td>{user.Email}</Td>
                <Td>{user.PhoneNumber}</Td>
                <Td>
                  <span
                    className={`status-box ${
                      user.IsActive ? "active" : "inactive"
                    }`}
                  >
                    {user.IsActive ? "Active" : "Inactive"}
                  </span>
                </Td>
                <Td>
                  <Tooltip
                    label="View Details"
                    aria-label="View Details"
                    placement="top"
                  >
                    <Button
                      size="sm"
                      colorScheme="blue"
                      mr={2}
                      onClick={() => viewDetails(user.Id)}
                      aria-label="View Details"
                    >
                      <Icon as={BiShow} />
                    </Button>
                  </Tooltip>
                  <Tooltip label="Remove license" placement="top">
                    <Button
                      size="sm"
                      colorScheme="red"
                      mr={2}
                      onClick={() => openDeleteModal(user.Id)}
                    >
                      <i className="bi bi-trash3"></i>
                    </Button>
                  </Tooltip>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <TenantDetailsForm
        isOpen={isOpen}
        onClose={closeModal}
        onSave={fetchTenants}
        tenant={selectedTenant}
      />

      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <WarningIcon color="red.500" boxSize={5} mr={3} />
            Delete Tenant
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex align="center" gap={2}>
              <Text>
                Are you sure you want to delete tenant{" "}
                <strong>
                  {tenants.find((t) => t.Id === deleteTenantId)?.Email}
                </strong>
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={confirmDelete}>
              Yes, Delete
            </Button>
            <Button variant="ghost" onClick={closeDeleteModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TenantList;
